import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface OrderItem {
  id: number;
  name: string;
  price: string;
  quantity: number;
  image: string;
}

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  items: OrderItem[];
  total_amount: number;
  payment_method: string;
  payment_status: string;
  trade_no: string | null;
  created_at: string;
  updated_at: string;
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchOrderNo, setSearchOrderNo] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (searchEmail) {
        query = query.eq('customer_email', searchEmail);
      }
      if (searchOrderNo) {
        query = query.eq('order_number', searchOrderNo);
      }

      const { data, error } = await query;

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('查詢訂單失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrders();
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      credit: '信用卡',
      atm: 'ATM 轉帳',
      cvs: '超商代碼',
    };
    return methods[method] || method;
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: {
        label: '待付款',
        className: 'bg-yellow-100 text-yellow-800',
      },
      paid: {
        label: '已付款',
        className: 'bg-green-100 text-green-800',
      },
      failed: {
        label: '付款失敗',
        className: 'bg-red-100 text-red-800',
      },
      cancelled: {
        label: '已取消',
        className: 'bg-gray-100 text-gray-800',
      },
    };
    const config = statusConfig[status] || {
      label: status,
      className: 'bg-gray-100 text-gray-800',
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoJsonLd type="orders" />
      {/* 頂部導航 */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/4f3f06d226654fda02a3ba64a379f0f2/363c495c98d5f335626050bd4b7a3150.jpeg"
                alt="Monceau Fleurs"
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">
                Monceau Fleurs
              </span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首頁
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">訂單記錄</h1>

        {/* 搜尋區塊 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">查詢訂單</h2>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                電子郵件
              </label>
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="輸入您的電子郵件"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                訂單編號
              </label>
              <input
                type="text"
                value={searchOrderNo}
                onChange={(e) => setSearchOrderNo(e.target.value)}
                placeholder="輸入訂單編號"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line mr-2"></i>
                查詢
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchEmail('');
                  setSearchOrderNo('');
                  fetchOrders();
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
              >
                清除
              </button>
            </div>
          </form>
        </div>

        {/* 訂單列表 */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">載入中...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
              <i className="ri-file-list-3-line text-5xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              尚無訂單記錄
            </h3>
            <p className="text-gray-600 mb-6">
              {searchEmail || searchOrderNo
                ? '找不到符合條件的訂單'
                : '您還沒有任何訂單'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line"></i>
              開始購物
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* 訂單標題 */}
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() =>
                    setExpandedOrder(
                      expandedOrder === order.id ? null : order.id
                    )
                  }
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm text-gray-500">訂單編號</p>
                        <p className="font-semibold text-gray-900">
                          {order.order_number}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">訂單金額</p>
                        <p className="font-bold text-teal-600">
                          NT$ {order.total_amount.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">付款方式</p>
                        <p className="font-medium">
                          {getPaymentMethodLabel(order.payment_method)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 mb-1">付款狀態</p>
                        {getPaymentStatusBadge(order.payment_status)}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">建立時間</p>
                        <p className="font-medium text-sm">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i
                          className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform ${
                            expandedOrder === order.id ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 訂單詳情 */}
                {expandedOrder === order.id && (
                  <div className="border-t border-gray-100 p-6 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* 顧客資訊 */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">
                          顧客資訊
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p>
                            <span className="text-gray-500">姓名：</span>
                            {order.customer_name}
                          </p>
                          <p>
                            <span className="text-gray-500">電子郵件：</span>
                            {order.customer_email}
                          </p>
                          <p>
                            <span className="text-gray-500">電話：</span>
                            {order.customer_phone}
                          </p>
                          {order.trade_no && (
                            <p>
                              <span className="text-gray-500">交易編號：</span>
                              {order.trade_no}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* 商品明細 */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">
                          商品明細
                        </h4>
                        <div className="space-y-3">
                          {order.items?.map((item, index) => (
                            <div key={index} className="flex gap-3">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-14 h-14 object-cover rounded"
                              />
                              <div className="flex-1">
                                <p className="font-medium text-sm">
                                  {item.name}
                                </p>
                                <p className="text-gray-500 text-sm">
                                  {item.price} x {item.quantity}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
